/**
 * `<ImportPanel>` — Settings → Import tab (Phase 2, 2026-05-17; U4 restyle 2026-07-04).
 *
 * Drag-drop / click-to-pick a .zip of markdown notes; POSTs same-origin to
 * `/api/v1/import` (the SWA Function proxy injects the bearer + forwards to the
 * Container App). Renders the import report when done.
 *
 * Supported zip shapes (all funnel through the backend's MarkdownImporter — defensive
 * about missing frontmatter keys):
 *   - Joplin RAW export (`File > Export all > RAW` in Joplin desktop)
 *   - Obsidian vault (zip the vault folder; wikilinks resolve)
 *   - Logseq graph (similar to Obsidian)
 *   - Any folder of `.md` files with or without YAML frontmatter
 *
 * For larger exports the request can run for tens of seconds before the response
 * lands — we show an in-flight indicator and disable the drop zone.
 *
 * U4: all layout/styling moved from inline `style={{…}}` to `.import-*` classes
 * (app.css §Settings); buttons on the canonical `.btn` system.
 */

import { useCallback, useState } from 'react';
import { Panel } from '@/components';

interface ImportReport {
  files_seen: number;
  imported: number;
  skipped: number;
  wikilinks_resolved: number;
  deferred_links: number;
  errors: { file: string; reason: string; detail: string }[];
}

type ImportState =
  | { kind: 'idle' }
  | { kind: 'uploading'; filename: string; sizeBytes: number }
  | { kind: 'success'; filename: string; report: ImportReport }
  | { kind: 'error'; filename: string; message: string };

const ACCEPT_TYPES = '.zip,application/zip,application/x-zip-compressed';
const MAX_BYTES = 100 * 1024 * 1024; // 100 MB — matches the SWA Function payload cap.

function formatBytes(b: number): string {
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
  return (b / (1024 * 1024)).toFixed(1) + ' MB';
}

async function postImport(file: File): Promise<ImportReport> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  // Same-origin POST: SWA Function proxy at /api/{*path} handles routing + auth-injection.
  const resp = await fetch('/api/v1/import', { method: 'POST', body: fd });
  const text = await resp.text();
  let parsed: { ok?: boolean; data?: ImportReport; error?: { code: string; message: string } };
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Backend returned non-JSON (HTTP ' + resp.status + '). First 200 chars: ' + text.slice(0, 200));
  }
  if (!resp.ok || parsed.ok === false) {
    const msg = parsed.error?.message ?? ('HTTP ' + resp.status);
    throw new Error(msg);
  }
  if (!parsed.data) {
    throw new Error('Backend response missing the report payload.');
  }
  return parsed.data;
}

export function ImportPanel(): JSX.Element {
  const [state, setState] = useState<ImportState>({ kind: 'idle' });
  const [dragOver, setDragOver] = useState(false);

  const onFileSelected = useCallback(async (file: File): Promise<void> => {
    if (file.size > MAX_BYTES) {
      setState({
        kind: 'error',
        filename: file.name,
        message: `File is ${formatBytes(file.size)} — the import path caps at 100 MB. Split the export or use the direct-blob upload path (coming soon).`,
      });
      return;
    }
    setState({ kind: 'uploading', filename: file.name, sizeBytes: file.size });
    try {
      const report = await postImport(file);
      setState({ kind: 'success', filename: file.name, report });
    } catch (e) {
      setState({ kind: 'error', filename: file.name, message: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void onFileSelected(f);
  }, [onFileSelected]);

  const onPick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void onFileSelected(f);
  }, [onFileSelected]);

  const isBusy = state.kind === 'uploading';
  const dropClass = [
    'import-drop',
    dragOver ? 'import-drop--over' : '',
    isBusy ? 'import-drop--busy' : '',
  ].join(' ').trim();

  return (
    <Panel title="Import notes (bulk markdown ZIP)" anchorId="import">
      <p className="import-intro">
        Drag in a <strong>.zip</strong> of markdown notes — Joplin RAW export, Obsidian
        vault, Logseq graph, or any folder of <code>.md</code> files. YAML frontmatter is
        read where present; missing keys are derived from filename / file mtime.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={dropClass}
      >
        <div className="import-drop__line">
          {isBusy
            ? `Uploading ${state.filename} (${formatBytes(state.sizeBytes)})…`
            : 'Drop a .zip here, or'}
        </div>
        {!isBusy && (
          <label className="import-drop__pick">
            click to pick a file
            <input
              type="file"
              accept={ACCEPT_TYPES}
              onChange={onPick}
              className="import-drop__input"
            />
          </label>
        )}
        {isBusy && (
          <div className="import-drop__hint">
            Larger exports can take 30–60 seconds. Don't navigate away.
          </div>
        )}
      </div>

      {state.kind === 'success' && (
        <div className="import-result import-result--ok">
          <div className="import-result__title">
            Imported {state.report.imported} of {state.report.files_seen} files from <code>{state.filename}</code>
          </div>
          <div className="import-result__meta">
            Skipped: {state.report.skipped} · Wikilinks resolved: {state.report.wikilinks_resolved} · Deferred: {state.report.deferred_links}
          </div>
          {state.report.errors.length > 0 && (
            <details className="import-result__errors">
              <summary>
                {state.report.errors.length} error{state.report.errors.length === 1 ? '' : 's'}
              </summary>
              <ul>
                {state.report.errors.map((e, i) => (
                  <li key={i}>
                    <strong>{e.file}</strong>: {e.reason} — {e.detail}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <button
            type="button"
            className="btn btn--sm import-result__action"
            onClick={() => setState({ kind: 'idle' })}
          >
            Import another
          </button>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="import-result import-result--err">
          <div className="import-result__title">
            Import failed: <code>{state.filename}</code>
          </div>
          <div className="import-result__message">{state.message}</div>
          <button
            type="button"
            className="btn btn--sm import-result__action"
            onClick={() => setState({ kind: 'idle' })}
          >
            Try again
          </button>
        </div>
      )}
    </Panel>
  );
}
